import { useState } from "react";

import { Check, ChevronDown, Copy } from "lucide-react";
import { useDispatch } from "react-redux";

import { CocEntry } from "@constants/codeOfConduct";

import { useCopyToClipboard } from "@hooks/use-copy-to-clipboard";

import { setMessage } from "@redux/notification";

import { cn } from "@utils/ui";

type Props = {
  entry: CocEntry;
};

/**
 * A single Code of Conduct declaration card: service-line label, the
 * (expandable) declaration text, its category, and a copy-to-clipboard action.
 */
export const EntryCard = ({ entry }: Props) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  // The declaration may hold multiple paragraphs separated by a blank line
  // (service-specific opening + core expert-duty paragraph). Split them so we
  // can render each as its own <p> with real spacing when expanded — a plain
  // `whitespace-pre-wrap` block loses the blank-line gap under `line-clamp`.
  const paragraphs = entry.text.split(/\n{2,}/).map((p) => p.trim());

  const handleCopy = () =>
    copy(entry.text, {
      onSuccess: () =>
        dispatch(
          setMessage({
            type: "success",
            content: "Code of Conduct copied to clipboard.",
          })
        ),
      onError: () =>
        dispatch(
          setMessage({
            type: "error",
            content:
              "Couldn't copy to clipboard. Select the text and copy manually.",
          })
        ),
    });

  return (
    <div className="flex h-full w-full flex-col gap-0.5 rounded-xl border bg-background-white p-3 dark:border-dark-50">
      <div className="flex flex-col pb-1">
        <p className="text-xs font-semibold leading-[18px] tracking-[-0.15px] text-foreground">
          {entry.label}
        </p>
        {entry.serviceKind.trim() && (
          <p className="py-0.5 text-[10px] font-normal leading-normal text-foreground-muted">
            {entry.serviceKind}
          </p>
        )}
        {expanded ? (
          <div className="flex flex-col gap-2">
            {paragraphs.map((paragraph, i) => (
              <p
                key={i}
                className="text-justify text-xs font-normal leading-[18px] tracking-[-0.15px] text-foreground-muted"
              >
                {paragraph}
              </p>
            ))}
          </div>
        ) : (
          // Collapsed: clamp the joined text to 3 lines. line-clamp needs a
          // single -webkit-box element, so we can't clamp across separate <p>s
          // here — join with a space for the preview.
          <p className="line-clamp-3 text-justify text-xs font-normal leading-[18px] tracking-[-0.15px] text-foreground-muted">
            {paragraphs.join(" ")}
          </p>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium leading-[18px] text-primary"
        >
          <span className="underline">{expanded ? "Show less" : "Show more"}</span>
          <ChevronDown
            className={cn("size-3.5 transition-transform", expanded && "rotate-180")}
          />
        </button>
      </div>

      <div className="mt-auto flex items-center justify-between pt-1">
        <p className="min-w-0 flex-1 truncate text-[10px] font-medium text-foreground">
          {entry.category}
        </p>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copy ${entry.label} declaration`}
          className={cn(
            "inline-flex size-3.5 shrink-0 items-center justify-center transition-colors",
            copied ? "text-success" : "text-foreground-muted hover:text-foreground"
          )}
          title={copied ? "Copied" : "Copy"}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      </div>
    </div>
  );
};
